import React, { ElementType } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Stack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import about_us from "./about_us.svg";
import career from "./career.svg";
import faqs from "./faqs.svg";
import contact_us from "./contact_us.svg";
import { logEvent } from "helpers/analytics";

interface NavbarProps {
  banner?: ElementType;
}

const Navbar: React.FC<NavbarProps> = ({ banner: Banner }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  const isActivePage = (urlToCheck: string) => {
    return location.pathname === urlToCheck;
  };

  const menu = {
    homepage: {
      url: "/",
    },
    business: {
      url: "/business",
    },
    homeOwners: {
      url: "/homeowners",
    },
    artisans: {
      url: "/artisans",
    },
    aboutUs: {
      url: "/about",
    },
    career: {
      url: "https://laborhack.grovehr.com/careers",
    },
    faqs: {
      url: "/faqs",
    },
    contactUs: {
      url: "/contact",
    },
    blog: {
      url: "https://blog.laborhack.com/",
    },
    materials: {
      url: "https://bit.ly/40n5Pv8",
    },
    login: {
      url: "/auth/login",
    },
  };

  return (
    <Stack
      bg="#FBFBFB"
      color="white"
      position="sticky"
      width="full"
      zIndex={999}
      top={0}
    >
      <Flex
        px={5}
        h={16}
        alignItems={"center"}
        justifyContent={"space-between"}
        height="6.75rem"
        m={{ base: 0, xl: "0 90px" }}
        display={isOpen ? "none" : "flex"}
      >
        <RouterLink to="/">
          <Image
            src="./new-logo.svg"
            alt="LaborHack"
            height="24px"
            width="174px"
          />
        </RouterLink>
        <Flex
          display={{
            base: "none",
            md: "none",
            lg: "none",
            xl: "flex",
            width1279px: "none",
            width1428px: "flex",
          }}
          alignItems={"center"}
          justify="center"
          maxWidth="60%"
          margin="auto"
        >
          <Link
            px={4}
            as={RouterLink}
            to="/business"
            color={
              isActivePage(menu.business.url) ? "black" : "inactiveTextColor"
            }
            _hover={{ textDecoration: "none", color: "#949494" }}
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
          >
            <Text>Businesses</Text>
          </Link>
          <Link
            px={4}
            as={RouterLink}
            to="/homeowners"
            color={
              isActivePage(menu.homeOwners.url) ? "black" : "inactiveTextColor"
            }
            _hover={{ textDecoration: "none", color: "#949494" }}
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
          >
            <Text>Homeowners</Text>
          </Link>
          <Link
            px={4}
            as={RouterLink}
            to="/artisans"
            color={
              isActivePage(menu.artisans.url) ? "black" : "inactiveTextColor"
            }
            _hover={{ textDecoration: "none", color: "#949494" }}
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
          >
            <Text>Artisans</Text>
          </Link>

          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              px={4}
              bgColor="transparent"
              color={
                isActivePage(menu.aboutUs.url) ||
                isActivePage(menu.faqs.url) ||
                isActivePage(menu.contactUs.url)
                  ? "black"
                  : "inactiveTextColor"
              }
              fontSize="20px"
              fontStyle="normal"
              fontWeight={500}
              lineHeight="28px"
            >
              Company
            </MenuButton>
            <MenuList
              color="white"
              display="Grid"
              width="612px"
              position="relative"
              right={{
                base: "21rem",
                md: "21rem",
                lg: "21rem",
                xl: "21rem",
                "2xl": "21rem",
              }}
              top="21px"
              boxShadow="-1px 12px 34px -17px rgba(183, 183, 183, 0.70)"
              zIndex={9999}
              css={{
                "@media (min-width: 1280px) and (max-width: 1375px)": {
                  right: "-21rem",
                },
              }}
            >
              <MenuItem color="black" onClick={() => {}} gridColumn="1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack as={RouterLink} to="/about" spacing={18}>
                    <Image src={about_us} alt="About Us" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        About Us
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                      >
                        Learn more about who we are
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" gridColumn="2">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack
                    as={"a"}
                    href="https://laborhack.grovehr.com/careers"
                    spacing={18}
                  >
                    <Image src={career} alt="Career" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        Career
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                      >
                        Join the LaborHack team
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="1/span1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack as={RouterLink} to="/faqs" spacing={18}>
                    <Image src={faqs} alt="Faqs" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        FAQs
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                      >
                        Get answers to your questions
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
              <MenuItem color="black" onClick={() => {}} gridColumn="2/span 1">
                <Flex color="black" onClick={() => {}} p="16px">
                  <HStack as={RouterLink} to="/contact" spacing={18}>
                    <Image src={contact_us} alt="Contact Us" />
                    <Box>
                      <Text
                        fontSize="20px"
                        fontStyle="normal"
                        fontWeight="500"
                        lineHeight="21px"
                      >
                        {" "}
                        Contact Us
                      </Text>
                      <Text
                        fontSize="14px"
                        fontStyle="normal"
                        fontWeight="400"
                        lineHeight="21px"
                      >
                        Reach out to us anytime
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>

          <Link
            px={4}
            href="https://blog.laborhack.com/"
            color={isActivePage(menu.blog.url) ? "black" : "inactiveTextColor"}
            _hover={{ textDecoration: "none", color: "#949494" }}
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
          >
            <Text>Blog</Text>
          </Link>
          <Link
            px={4}
            href="https://bit.ly/40n5Pv8"
            color={
              isActivePage(menu.materials.url) ? "black" : "inactiveTextColor"
            }
            _hover={{ textDecoration: "none", color: "#949494" }}
            fontSize="20px"
            fontStyle="normal"
            fontWeight={500}
            lineHeight="28px"
            target="_blank"
            onClick={() => logEvent("Materials_link_clicked")}
          >
            <Text>Materials</Text>
          </Link>
        </Flex>
        <Flex
          alignItems={"center"}
          justify="end"
          display={{
            base: "none",
            md: "none",
            lg: "none",
            xl: "flex",
            width1279px: "none",
            width1428px: "flex",
          }}
        >
          <Button
            as={RouterLink}
            to="auth/login"
            variant="outline"
            border="2px solid #153243"
            borderRadius={0}
            padding="12px 37.5px"
            width="128px"
            height="52px"
            color="prussianBlue.500"
            fontSize="20px"
            fontStyle="normal"
            fontWeight="500"
            _hover={{ textDecoration: "none", color: "prussianBlue.500" }}
          >
            Login
          </Button>
        </Flex>
        <IconButton
          display={{
            base: "flex",
            md: "flex",
            lg: "flex",
            xl: "none",
            width1279px: "flex",
            width1428px: "none",
          }}
          onClick={() => onOpen()}
          icon={<HamburgerIcon boxSize={6} />}
          aria-label={""}
          bgColor="transparent"
          color="flatteredFlamingo.500"
        />
      </Flex>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay>
          <DrawerContent
            display={{
              base: "flex",
              md: "flex",
              lg: "flex",
              xl: "none",
              width1279px: "flex",
              width1428px: "none",
            }}
          >
            <DrawerCloseButton />
            <DrawerHeader pb="16px" borderBottom="1px solid #F3F3F3">
              {" "}
              <Image
                src="./new-logo.svg"
                alt="LaborHack"
                height="24px"
                width="174px"
              />
            </DrawerHeader>
            <DrawerBody padding={0}>
              <Stack spacing={0}>
                <Link
                  as={RouterLink}
                  to="/business"
                  p="16px"
                  borderBottom="1px solid #F3F3F3"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "Inherit" }}
                  onClick={onClose}
                >
                  Businesses
                </Link>
                <Link
                  as={RouterLink}
                  to="/homeowners"
                  p="16px"
                  borderBottom="1px solid #F3F3F3"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "Inherit" }}
                  onClick={onClose}
                >
                  Homeowners
                </Link>
                <Link
                  as={RouterLink}
                  to="artisans"
                  p="16px"
                  borderBottom="1px solid #F3F3F3"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "Inherit" }}
                  onClick={onClose}
                >
                  Artisans
                </Link>
                <Accordion allowToggle>
                  <AccordionItem>
                    <AccordionButton p={0}>
                      <Box
                        flex="1"
                        textAlign="left"
                        p="16px"
                        color="#0E0302"
                        fontFamily="AeonikPro"
                        fontSize="18px"
                        fontStyle="normal"
                        fontWeight={500}
                        lineHeight="24px"
                        _hover={{ textDecoration: "none", color: "Inherit" }}
                      >
                        Company
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Flex color="black" onClick={() => {}} p="16px">
                        <HStack
                          as={RouterLink}
                          to="/about"
                          spacing={18}
                          _hover={{
                            textDecoration: "none",
                            color: "inherit",
                          }}
                          onClick={onClose}
                        >
                          <Image src={about_us} alt="About Us" />
                          <Box>
                            <Text
                              fontSize="14px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="21px"
                            >
                              {" "}
                              About Us
                            </Text>
                            <Text
                              fontSize="12px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="19px"
                              color="#5E5E5E"
                            >
                              Learn more about who we are
                            </Text>
                          </Box>
                        </HStack>
                      </Flex>
                      <Flex color="black" onClick={() => {}} p="16px">
                        <HStack
                          as={"a"}
                          href="https://laborhack.grovehr.com/careers"
                          spacing={18}
                          onClick={onClose}
                        >
                          <Image src={career} alt="Career" />
                          <Box>
                            <Text
                              fontSize="14px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="21px"
                            >
                              {" "}
                              Career
                            </Text>
                            <Text
                              fontSize="12px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="19px"
                              color="#5E5E5E"
                            >
                              Join the LaborHack team
                            </Text>
                          </Box>
                        </HStack>
                      </Flex>
                      <Flex color="black" onClick={() => {}} p="16px">
                        <HStack
                          as={RouterLink}
                          to="/faqs"
                          spacing={18}
                          onClick={onClose}
                        >
                          <Image src={faqs} alt="Faqs" />
                          <Box>
                            <Text
                              fontSize="14px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="21px"
                            >
                              {" "}
                              FAQs
                            </Text>
                            <Text
                              fontSize="12px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="19px"
                              color="#5E5E5E"
                            >
                              Get answers to your questions
                            </Text>
                          </Box>
                        </HStack>
                      </Flex>
                      <Flex color="black" p="16px">
                        <HStack
                          spacing={18}
                          as={RouterLink}
                          to="/contact"
                          onClick={onClose}
                        >
                          <Image src={contact_us} alt="Contact Us" />
                          <Box>
                            <Text
                              fontSize="14px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="21px"
                            >
                              {" "}
                              Contact Us
                            </Text>
                            <Text
                              fontSize="12px"
                              fontStyle="normal"
                              fontWeight="500"
                              lineHeight="19px"
                              color="#5E5E5E"
                            >
                              Reach out to us anytime
                            </Text>
                          </Box>
                        </HStack>
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>

                <Link
                  href="https://blog.laborhack.com/"
                  borderBottom="1px solid #F3F3F3"
                  p="16px"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "Inherit" }}
                  onClick={onClose}
                >
                  Blog
                </Link>
                <Link
                  href="https://bit.ly/40n5Pv8"
                  borderBottom="1px solid #F3F3F3"
                  p="16px"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "Inherit" }}
                  target="_blank"
                  onClick={() => logEvent("Materials_link_clicked")}
                >
                  Materials
                </Link>
                <Link
                  as={RouterLink}
                  to={menu.login.url}
                  p="16px"
                  borderBottom="1px solid #F3F3F3"
                  fontFamily="AeonikPro"
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={500}
                  lineHeight="24px"
                  _hover={{ textDecoration: "none", color: "inherit" }}
                  onClick={onClose}
                >
                  Login
                </Link>
              </Stack>
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
      {Banner && <Banner width="full" display={isOpen ? "none" : "flex"} />}
    </Stack>
  );
};

export default Navbar;
